/**
 * SOLOMON — ROOT APP COMPONENT
 * Phase 1 / Day 9: Login & Authentication
 *
 * The app is Solomon. There is no traditional home screen.
 * You open solomonhq.com and Solomon IS the interface.
 *
 * Day 9: Auth gate added — shows LoginPage if not authenticated.
 * After login, fades to the main Solomon interface.
 */

import { useState, useEffect, useRef, useCallback, useMemo, startTransition } from 'react';
import { useSolomon, ACTIONS }          from './context/SolomonContext';
import { useAuth }                      from './context/AuthContext';
import { useElevenLabs }                from './hooks/useElevenLabs';
import { useSpeechRecognition }         from './hooks/useSpeechRecognition';
import { useCapabilityStatus }          from './hooks/useCapabilityStatus';
import ChatInterface                    from './components/ChatInterface';
import LoginPage                        from './components/LoginPage';
import ResearchPanel                    from './components/ResearchPanel';
import LearnPanel                       from './components/LearnPanel';
import WholesaleWorkspace               from './components/Wholesale/WholesaleWorkspace';
import AutomationsWorkspace             from './components/Automations/AutomationsWorkspace.jsx';
import { mcpOAuthNotice } from './components/SettingsPanel/mcpCopy.js';
import { visiblePendingActions } from './context/continuation.js';
import { parseChatDeepLink } from './utils/chatDeepLink.js';
import './styles/globals.css';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';

export default function App() {
  const { isAuthenticated, isLoading: authLoading, user, logout, authFetch } = useAuth();
  const {
    messages,
    isThinking,
    thinkingModel,
    isSpeaking,
    isLoadingHistory,
    voiceEnabled,
    selectedVoiceId,
    conversationId,
    error,
    notice,
    sendMessage,
    stopGeneration,
    addMessage,
    setVoiceEnabled,
    setSelectedVoiceId,
    newConversation,
    regenerateWithMode,
    fetchAndDisplayBriefing,
    showBriefingForDate,
    // Task 3: edit & resend a prior user message
    pendingDraft,
    editMessage,
    consumeDraft,
    dispatch,
    // Ephemeral chat attachments (sticky-for-conversation)
    attachments,
    attachDocument,
    clearAttachment,
    clearAttachments,
    // Pending images (ephemeral one-turn vision attachments)
    pendingImages,
    attachImage,
    clearPendingImage,
    clearPendingImages,
    // Phase 2A: action guardrails
    pendingActions,
    activeUndoBanner,
    approveAction,
    cancelAction,
    undoAction,
    dismissUndoBanner,
    // Phase 2: integrations
    gmailConnected,
    checkGmailStatus,
    connectGmail,
    disconnectGmail,
    // Settings panel open/section — lifted into the context (spec §5) so any
    // component (an ActionCard deep in the message list included) can open a
    // section without a prop chain.
    settingsOpen,
    settingsSection,
    openSettings,
    closeSettings,
    toggleSettings,
  } = useSolomon();

  // Sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const toggleSidebar = useCallback(() => setSidebarOpen(o => !o), []);

  // Active tool — single source of truth for which modal is open.
  // null | 'signals' | 'memory' | ...
  // Adding a tool = one entry in capabilities/registry.js + one render
  // case in ChatInterface. No new state.
  const [activeTool, setActiveTool] = useState(null);

  // Teach Solomon (LearnPanel) — its OWN boolean, deliberately not an
  // `activeTool` value. ToolModal unmounts its children on close and Learn
  // owns an in-flight transcription+ingest (status / progress / abortRef), so
  // it is mounted once below and toggled with `hidden`. MemoryPanel, which
  // holds nothing in flight, does go through `activeTool` and unmounts.
  // Opened only from the Capabilities map (`handleCapabilityInvoke` below) —
  // the chrome no longer carries a Learn button.
  const [learnOpen, setLearnOpen] = useState(false);
  const closeLearn = useCallback(() => setLearnOpen(false), []);

  // Lead deep-link target — set by CallQueuePanel via `solomon:open-lead`
  // event when the operator picks a row to call. Consumed by LeadsView via
  // the `initialLeadId` prop; cleared after first read.
  const [pendingLeadId, setPendingLeadId] = useState(null);
  // Group 5 PR A: a Discord notification's link lands on one automation's run history.
  const [pendingAutomationId, setPendingAutomationId] = useState(null);

  useEffect(() => {
    const handler = (e) => {
      if (e?.detail?.leadId) {
        setPendingLeadId(e.detail.leadId);
        setWholesalePanelOpen(true); // open the Wholesale panel; it jumps to Leads on the pending id
      }
    };
    window.addEventListener('solomon:open-lead', handler);
    return () => window.removeEventListener('solomon:open-lead', handler);
  }, []);

  // Research panel — left-side persistent column. Default CLOSED.
  // Preference persists to 'solomon-research-panel-preference' in localStorage.
  // Smart auto-open (based on active task count) lands in the functionality commit.
  const [researchPanelOpen, setResearchPanelOpen] = useState(() => {
    return localStorage.getItem('solomon-research-panel-preference') === 'open';
  });
  const toggleResearchPanel = useCallback(() => {
    setResearchPanelOpen(prev => {
      const next = !prev;
      localStorage.setItem('solomon-research-panel-preference', next ? 'open' : 'closed');
      return next;
    });
  }, []);

  // Wholesale workspace panel — mirrors the Research panel toggle pattern.
  const [wholesalePanelOpen, setWholesalePanelOpen] = useState(() => {
    return localStorage.getItem('solomon-wholesale-panel-preference') === 'open';
  });
  const toggleWholesalePanel = useCallback(() => {
    setWholesalePanelOpen(prev => {
      const next = !prev;
      localStorage.setItem('solomon-wholesale-panel-preference', next ? 'open' : 'closed');
      return next;
    });
  }, []);

  // Automations workspace panel — mirrors the Wholesale panel toggle pattern.
  const [automationsTab, setAutomationsTab] = useState(null);
  const [automationsPanelOpen, setAutomationsPanelOpen] = useState(() => {
    return localStorage.getItem('solomon-automations-panel-preference') === 'open';
  });
  const toggleAutomationsPanel = useCallback(() => {
    setAutomationsPanelOpen(prev => {
      const next = !prev;
      localStorage.setItem('solomon-automations-panel-preference', next ? 'open' : 'closed');
      return next;
    });
  }, []);

  // Capability surface — the status hook is mounted HERE, exactly once, and
  // passed down as props. The hero unmounts the moment the first message
  // lands, so a second instance in the chat header would refetch and flash a
  // bare strip at exactly that transition.
  const { derived: capabilityState, refresh: refreshCapabilities } = useCapabilityStatus(authFetch);
  const [capabilitiesOpen, setCapabilitiesOpen] = useState(false);
  const openCapabilities = useCallback(() => {
    setCapabilitiesOpen(true);
    setLearnOpen(false);       // see openWorkspace — Learn would float above this
    refreshCapabilities();     // clears a stale dormant badge after a reconnect elsewhere
  }, [refreshCapabilities]);
  const closeCapabilities = useCallback(() => setCapabilitiesOpen(false), []);

  // Explicit OPEN, never toggle: the toggle* handlers flip `prev` (and persist
  // that flip to localStorage), so invoking "Wholesale" from the capability map
  // while it is already open would CLOSE it.
  const openWorkspace = useCallback((target, tab = null) => {
    setAutomationsTab(tab);
    // Close Learn first. `.learn-dropdown` is position:fixed at --z-modal with
    // NO scrim, and it mounts last inside `.solomon-app` — neither that element
    // nor `.app-layout` creates a stacking context, so DOM order decides and
    // Learn paints ABOVE `.cap-panel` and `.ws-panel`, which share the same
    // z-index. It would float over a full-viewport workspace while the modal's
    // Tab trap made its controls keyboard-unreachable but still mouse-clickable.
    // Closing only sets isOpen=false — Learn stays mounted, so an in-flight
    // ingest is untouched.
    setLearnOpen(false);
    if (target === 'wholesale')   setWholesalePanelOpen(true);
    if (target === 'automations') setAutomationsPanelOpen(true);
    if (target === 'research')    setResearchPanelOpen(true);
  }, []);

  // MCP management lives in Automations › Connections, not in the Settings
  // accordion. A deep-link that names 'mcp' is routed there — otherwise the
  // "Manage connections" link on a gated card opens a panel with no MCP section.
  useEffect(() => {
    if (settingsOpen && settingsSection === 'mcp') {
      closeSettings();
      openWorkspace('automations', 'connections');
    }
  }, [settingsOpen, settingsSection, closeSettings, openWorkspace]);

  const handleCapabilityInvoke = useCallback((item) => {
    // Always close first: a tool invoke opens ToolModal at the same --z-modal,
    // and the popup's focus management must release before the composer can
    // take focus.
    setCapabilitiesOpen(false);

    const invoke = item.action || item.invoke;
    if (!invoke) return;

    if (invoke.type === 'prefill') {
      // NO isThinking GUARD — deliberately. The spec's rule is "never a silently
      // inert input", and an earlier version refused to prefill mid-stream on the
      // assumption that the composer was disabled while a response streamed. It
      // is not: InputBar's textarea is `disabled={disabled || isListening ||
      // isUploading}` and ChatInterface passes no `disabled` at all, so the box
      // stays fully typeable. The guard therefore blocked the map's primary
      // action — every native tool row and every MCP row — at exactly the moment
      // the operator is idle enough to browse it, while a perfectly usable
      // textarea sat next to the refusal toast. The append at InputBar.jsx:75 is
      // safe mid-stream, so there is nothing to guard.
      // `editMessage` IS the pending-draft setter: pendingDraft is useState in
      // SolomonProvider, not reducer state, so there is no SET_PENDING_DRAFT
      // action to dispatch. App already threads pendingDraft/consumeDraft to
      // InputBar, which APPENDS the text without sending.
      editMessage(invoke.text || item.example);
    } else if (invoke.type === 'settings') {
      // `target` names the accordion section (e.g. the Google attention row's
      // 'integrations'); null just opens Settings collapsed.
      openSettings(invoke.target || null);
    } else if (invoke.type === 'workspace') {
      openWorkspace(invoke.target, invoke.tab || null);
    } else if (invoke.type === 'tool') {
      // Learn is not an activeTool — see the learnOpen declaration above.
      if (invoke.target === 'learn') setLearnOpen(true);
      else setActiveTool(invoke.target);
    }
  }, [editMessage, openWorkspace, openSettings]);

  // Auto-open: fired by ResearchPanel on mount when active tasks exist.
  // Does NOT write to localStorage — the user's explicit preference stays
  // intact across sessions where no active tasks are present.
  const handleAutoOpenResearchPanel = useCallback(() => {
    setResearchPanelOpen(true);
  }, []);

  // ---- Theme management ----
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem('solomon-theme') || 'dark';
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('solomon-theme', theme);
  }, [theme]);

  const toggleTheme = () => setTheme(t => t === 'dark' ? 'light' : 'dark');

  // ============================================================
  // ELEVENLABS TTS
  // ============================================================

  // Stable callbacks — must be memoized so speakText doesn't get
  // a new reference on every render (which would re-trigger the speak effect)
  const onSpeakStart = useCallback(() => {
    dispatch({ type: ACTIONS.SET_SPEAKING, payload: true });
  }, [dispatch]);

  const onSpeakEnd = useCallback(() => {
    dispatch({ type: ACTIONS.SET_SPEAKING, payload: false });
  }, [dispatch]);

  const onTTSError = useCallback((msg) => {
    console.warn('[Solomon/TTS]', msg);
    // Surface the message in the existing error toast and auto-clear after
    // 5s so a transient TTS notice doesn't linger like a blocking error.
    dispatch({ type: ACTIONS.SET_ERROR, payload: msg });
    setTimeout(() => dispatch({ type: ACTIONS.CLEAR_ERROR }), 5000);
  }, [dispatch]);

  const ttsCallbacks = useMemo(() => ({
    onSpeakStart,
    onSpeakEnd,
    onError: onTTSError,
  }), [onSpeakStart, onSpeakEnd, onTTSError]);

  const {
    speakText,
    stopSpeaking,
    fetchVoices,
    unlockAudio,
  } = useElevenLabs({ ...ttsCallbacks, authFetch });

  // Track which message we last spoke — ref avoids stale closure issues
  const lastSpokenIdRef   = useRef(null);
  const voiceEnabledRef   = useRef(voiceEnabled);
  const selectedVoiceRef  = useRef(selectedVoiceId);
  // Re-entrancy guard for handleSelectConversation
  const isLoadingHistoryRef = useRef(false);

  // Keep refs in sync with state (no re-renders from this)
  useEffect(() => { voiceEnabledRef.current  = voiceEnabled;    }, [voiceEnabled]);
  useEffect(() => { selectedVoiceRef.current = selectedVoiceId; }, [selectedVoiceId]);

  // Speak new assistant messages whenever the messages array changes
  useEffect(() => {
    if (!voiceEnabledRef.current)   return;   // voice is off
    if (!selectedVoiceRef.current)  return;   // no voice chosen yet

    const lastMsg = [...messages].reverse().find(m => m.role === 'assistant');
    if (!lastMsg) return;
    if (lastMsg.id === lastSpokenIdRef.current) return;   // already spoken this one

    lastSpokenIdRef.current = lastMsg.id;
    speakText(lastMsg.content, selectedVoiceRef.current);
  }, [messages, speakText]);   // only re-runs when messages or speakText changes

  // ============================================================
  // SPEECH RECOGNITION STT
  // ============================================================

  const handleSpeechResult = useCallback((transcript) => {
    const trimmed = transcript.trim();
    if (trimmed) sendMessage(trimmed);
  }, [sendMessage]);

  const {
    isListening: sttIsListening,
    isSupported: sttIsSupported,
    interimText,
    lastError:   sttError,
    toggleListening,
  } = useSpeechRecognition({
    onResult:     handleSpeechResult,
    autoSend:     true,
    silenceDelay: 1500,
  });

  // Orb error state (ruling 2, 2026-09-02): either toast condition puts the
  // lattice into its 3s error window. Edge-triggered inside LatticeCore, so a
  // sticky sttError cannot pin the orb red.
  const orbError = Boolean(error || sttError);

  // Sync STT listening state to context so the orb animates correctly
  useEffect(() => {
    dispatch({ type: ACTIONS.SET_LISTENING, payload: sttIsListening });
  }, [sttIsListening, dispatch]);

  const resumeCheckedRef = useRef(false);

  // ============================================================
  // AUTO-BRIEFING — show daily briefing on first visit each day
  // ============================================================

  const briefingCheckedRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || briefingCheckedRef.current) return;
    // Effects run in declaration order: this one runs before the URL-param effect
    // below resolves ?open=briefing and strips it, so the briefing shows once.
    if (new URLSearchParams(window.location.search).get('open') === 'briefing') { briefingCheckedRef.current = true; return; } // the deep link owns the display (Group 5 PR C)
    if (parseChatDeepLink(window.location.search)) { briefingCheckedRef.current = true; return; } // a chat deep link owns the display (Group 5 PR B)
    briefingCheckedRef.current = true;

    const today = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD local
    const lastBriefingDate = localStorage.getItem('solomon-last-briefing-date');
    if (lastBriefingDate === today) return; // Already shown today

    // Fetch and display the daily briefing — non-blocking.
    // Does NOT set thinking state so the app stays usable while fetching.
    // If it takes too long (20s timeout in fetchBriefing), it
    // simply doesn't show — user can still type or say "good morning".
    (async () => {
      try {
        const result = await fetchAndDisplayBriefing();
        if (result?.content) {
          localStorage.setItem('solomon-last-briefing-date', today);
          addMessage({
            role:         'assistant',
            content:      result.content,
            model:        result.model ?? null,
            detectedMode: 'briefing',
            isBriefing:   true,
            briefingType: 'morning',
          });
        }
      } catch {
        // Silently skip — user can still say "good morning"
      }
    })();
  }, [isAuthenticated, fetchAndDisplayBriefing, addMessage, dispatch]);

  // ============================================================
  // GMAIL STATUS CHECK + OAuth callback URL param detection
  // ============================================================

  useEffect(() => {
    if (!isAuthenticated) return;

    // Check Gmail connection status on load
    checkGmailStatus();

    // Detect ?gmail=connected|error from OAuth callback redirect
    const params = new URLSearchParams(window.location.search);
    const gmailParam = params.get('gmail');
    if (gmailParam) {
      if (gmailParam === 'connected') {
        dispatch({ type: ACTIONS.SET_GMAIL_CONNECTED, payload: true });
        dispatch({ type: ACTIONS.SET_ERROR, payload: null });
        // Show success briefly via the error toast mechanism (repurposed)
        dispatch({ type: ACTIONS.SET_ERROR, payload: 'Gmail connected successfully' });
        setTimeout(() => dispatch({ type: ACTIONS.CLEAR_ERROR }), 4000);
      } else if (gmailParam === 'error') {
        const msg = params.get('message') || 'Gmail connection failed';
        dispatch({ type: ACTIONS.SET_ERROR, payload: msg });
      }

      // Clean URL params without triggering navigation
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, '', cleanUrl);
    }

    // Detect ?mcp=connected|error from the MCP OAuth callback redirect (Issue 113).
    // Enum → copy only; the raw params are never rendered.
    const mcpParam = params.get('mcp');
    if (mcpParam) {
      const notice = mcpOAuthNotice({ mcp: mcpParam, reason: params.get('reason') });
      if (notice) {
        dispatch({
          type: notice.kind === 'success' ? ACTIONS.SET_NOTICE : ACTIONS.SET_ERROR,
          payload: notice.text,
        });
        if (notice.kind === 'success') setTimeout(() => dispatch({ type: ACTIONS.CLEAR_NOTICE }), 6000);
      }
      window.history.replaceState({}, '', window.location.pathname);
    }

    // Group 5 PR A: the Discord notification link —
    // ?open=automations&tab=<scheduled|automations|connections>&automation=<uuid>.
    // Enum-checked and UUID-checked; the raw params are never rendered.
    if (params.get('open') === 'automations') {
      const tabParam = params.get('tab');
      const automationParam = params.get('automation');
      const automationOk = Boolean(automationParam)
        && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(automationParam);
      // A valid automation id forces the tab that mounts the Manager; otherwise the
      // id would stay pending behind a tab that never consumes it.
      const tab = automationOk
        ? 'automations'
        : (['scheduled', 'automations', 'connections'].includes(tabParam) ? tabParam : null);
      if (automationOk) {
        // Postgres emits lowercase uuid text; the Manager compares with ===.
        setPendingAutomationId(automationParam.toLowerCase());
      }
      openWorkspace('automations', tab);
      window.history.replaceState({}, '', window.location.pathname);
    }

    // Group 5 PR C: the Discord briefing link — ?open=briefing&date=YYYY-MM-DD.
    // Date-checked; the raw param is never rendered. The auto-briefing effect
    // above already stood down for this URL, so this is the only display.
    if (params.get('open') === 'briefing') {
      const date = params.get('date');
      if (date && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
        if (date === new Date().toLocaleDateString('en-CA')) localStorage.setItem('solomon-last-briefing-date', date);
        showBriefingForDate(date);
      }
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [isAuthenticated, checkGmailStatus, dispatch, openWorkspace, showBriefingForDate]);

  // ============================================================
  // FILE UPLOAD (Whisper transcription)
  // ============================================================

  const [uploadProgress, setUploadProgress] = useState(null);
  const fileQueueRef    = useRef([]);
  const isProcessingRef = useRef(false);
  const uploadAbortRef  = useRef(null);

  // Cancel the current upload and clear the queue
  const cancelUpload = useCallback(() => {
    if (uploadAbortRef.current) {
      uploadAbortRef.current.abort();
      uploadAbortRef.current = null;
    }
    fileQueueRef.current = [];
    setUploadProgress({ stage: 'Upload cancelled', percent: 0 });
    dispatch({ type: ACTIONS.SET_THINKING, payload: false });
    setTimeout(() => setUploadProgress(null), 1500);
  }, [dispatch]);

  // Process one file through the Whisper transcription pipeline
  const processOneFile = useCallback(async (file) => {
    const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
    addMessage({
      role: 'user',
      content: `[Uploaded file: ${file.name} (${sizeMB} MB)]`,
    });

    const abortCtrl = new AbortController();
    uploadAbortRef.current = abortCtrl;

    setUploadProgress({ stage: `Uploading ${file.name}...`, percent: 5 });
    dispatch({ type: ACTIONS.SET_THINKING, payload: true });

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await authFetch(`${API_BASE}/api/learn/audio`, {
        method: 'POST',
        body: formData,
        signal: abortCtrl.signal,
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || `Upload failed: HTTP ${response.status}`);
      }

      const reader  = response.body.getReader();
      const decoder = new TextDecoder();
      let   buffer  = '';
      let   finalResult = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split('\n\n');
        buffer = events.pop();

        for (const rawEvent of events) {
          if (!rawEvent.trim()) continue;
          const lines     = rawEvent.split('\n');
          let   eventName = 'message';
          let   eventData = '';

          for (const line of lines) {
            if (line.startsWith('event: ')) eventName = line.slice(7).trim();
            else if (line.startsWith('data: ')) eventData = line.slice(6).trim();
          }

          try {
            const parsed = JSON.parse(eventData);
            if (eventName === 'progress') {
              const remaining = fileQueueRef.current.length;
              const queueLabel = remaining > 0 ? ` (${remaining} more queued)` : '';
              setUploadProgress({ stage: (parsed.detail || parsed.stage) + queueLabel, percent: parsed.percent || 0 });
            } else if (eventName === 'complete') {
              finalResult = parsed;
            } else if (eventName === 'error') {
              throw new Error(parsed.message || 'Transcription failed.');
            }
          } catch (parseErr) {
            if (parseErr.message && !parseErr.message.includes('JSON')) throw parseErr;
          }
        }
      }

      if (finalResult) {
        const dur = finalResult.durationEstimate || '?';
        const concepts = finalResult.key_concepts?.length || 0;
        const title = finalResult.source_title || file.name;
        const tags = finalResult.topic_tags?.join(', ') || '';
        addMessage({
          role:         'assistant',
          content:      `Got it. I transcribed ~${dur} minutes from "${title}." Learned ${concepts} concepts${tags ? ` about ${tags}` : ''}.\n\n${finalResult.summary || ''}`,
          model:        'whisper-1',
          detectedMode: 'command',
        });
      }
    } catch (err) {
      // AbortError from cancel — don't show as error
      if (err.name === 'AbortError') return;
      console.error('[Solomon/Upload]', err);
      addMessage({
        role:    'assistant',
        content: `I couldn't process ${file.name}: ${err.message}`,
        model:   'error',
      });
    }
  }, [authFetch, addMessage, dispatch]);

  // Drain the file queue sequentially
  const drainFileQueue = useCallback(async () => {
    if (isProcessingRef.current) return; // already draining
    isProcessingRef.current = true;

    while (fileQueueRef.current.length > 0) {
      const file = fileQueueRef.current.shift();
      await processOneFile(file);
    }

    setUploadProgress(null);
    dispatch({ type: ACTIONS.SET_THINKING, payload: false });
    isProcessingRef.current = false;
  }, [processOneFile, dispatch]);

  // Public handler: queue file(s) and start draining
  const handleFileUpload = useCallback((file) => {
    fileQueueRef.current.push(file);
    drainFileQueue();
  }, [drainFileQueue]);

  // ============================================================
  // LOAD PAST CONVERSATION
  // ============================================================

  const handleSelectConversation = useCallback(async (convId) => {
    if (!convId || convId === conversationId) return; // Already loaded
    if (isLoadingHistoryRef.current) return;          // Prevent double-clicks

    isLoadingHistoryRef.current = true;
    dispatch({ type: ACTIONS.SET_LOADING_HISTORY, payload: true });
    dispatch({ type: ACTIONS.CLEAR_ERROR });

    try {
      // Stop any in-flight TTS so loading a conversation doesn't start
      // reading the previous chat aloud
      stopSpeaking?.();

      // Attachments are scoped to the active conversation — drop them when
      // switching so a doc attached in conversation A never leaks into B.
      clearAttachments?.();

      const res = await authFetch(`${API_BASE}/api/chat/history/${convId}`);
      if (!res.ok) throw new Error('Failed to load conversation');
      const data = await res.json();

      const msgs = (data.messages || []).map(m => ({
        id:           m.id,
        responseId:   m.id,   // output-quality rating anchor (server messages.id)
        role:         m.role,
        content:      m.content,
        model:        m.model || null,
        confidence:   m.confidence ?? null,   // persisted so history ratings capture the #30 feature
        routingReason: m.routingReason || null, // persisted routing.reason — the badge's ↷ fallback marker survives a reload
        liveSources:  m.liveSources || null,  // live-web citations survive reloads
        timestamp:    m.timestamp || m.created_at,
        detectedMode: m.mode || null,
        // Preserve briefing styling on messages loaded from history. The
        // database doesn't store an isBriefing flag, but the per-message
        // mode is set to 'briefing' when the briefing is generated, so
        // we can recover the flag from that.
        isBriefing:   m.mode === 'briefing',
      }));

      // Mark the loaded conversation's last assistant message as already
      // "spoken" BEFORE we update messages. The speak-on-new-message effect
      // will see lastSpokenIdRef === lastMsg.id and bail, so loading a past
      // conversation never triggers TTS playback for an old reply.
      const lastAssistant = [...msgs].reverse().find(m => m.role === 'assistant');
      if (lastAssistant) lastSpokenIdRef.current = lastAssistant.id;

      // Wrap the big state replacement in startTransition so React can
      // schedule the (potentially expensive) bubble re-render around user
      // interactions instead of blocking the main thread.
      startTransition(() => {
        dispatch({ type: ACTIONS.SET_MESSAGES,        payload: msgs });
        dispatch({ type: ACTIONS.SET_CONVERSATION_ID, payload: convId });
      });
    } catch (err) {
      console.error('[Solomon/History]', err);
      dispatch({ type: ACTIONS.SET_ERROR, payload: 'Could not load that conversation.' });
    } finally {
      dispatch({ type: ACTIONS.SET_LOADING_HISTORY, payload: false });
      isLoadingHistoryRef.current = false;
    }
  }, [conversationId, authFetch, dispatch, stopSpeaking, clearAttachments]);

  // Group 5 PR B: the Discord link ?open=chat&conversation=<uuid>. Declared after
  // handleSelectConversation (its deps array reads it at render). A failed load
  // (foreign id -> 404) raises that handler's existing error toast.
  const chatDeepLinkRef = useRef(false);
  useEffect(() => {
    if (!isAuthenticated || chatDeepLinkRef.current) return;
    const link = parseChatDeepLink(window.location.search);
    if (!link) return;
    chatDeepLinkRef.current = true;
    window.history.replaceState({}, '', window.location.pathname);
    handleSelectConversation(link.conversationId);
  }, [isAuthenticated, handleSelectConversation]);

  // ============================================================
  // FRESH START — always open to the hero state on page load.
  // Previous conversations are accessible from the sidebar history.
  // Clear the persisted conversation so the auto-briefing can fire
  // (it skips when solomon-active-conversation is set).
  // ============================================================

  useEffect(() => {
    if (!isAuthenticated || resumeCheckedRef.current) return;
    resumeCheckedRef.current = true;

    // Clear any saved conversation — always start fresh
    localStorage.removeItem('solomon-active-conversation');
  }, [isAuthenticated]);

  // ============================================================
  // VOICE PANEL HANDLERS
  // ============================================================

  // Toggle voice output (ElevenLabs) on/off
  // unlockAudio() is called here because this is a direct user click —
  // this is what unlocks AudioContext in Safari for all future async plays
  // Wrap logout so conversation-scoped attachments never survive into the
  // next user's session on this device.
  const handleLogout = useCallback(() => {
    clearAttachments?.();
    clearPendingImages?.();
    logout();
  }, [clearAttachments, clearPendingImages, logout]);

  const handleToggleVoice = useCallback(() => {
    const next = !voiceEnabled;
    setVoiceEnabled(next);
    if (next) {
      unlockAudio();   // Safari: unlock AudioContext on the user gesture
    } else {
      stopSpeaking();  // stop mid-speech if user turns off
    }
  }, [voiceEnabled, setVoiceEnabled, unlockAudio, stopSpeaking]);

  // ============================================================
  // RENDER
  // ============================================================

  // While verifying the stored token, show nothing (prevents flash of login page)
  if (authLoading) {
    return (
      <div className="auth-loading-screen">
        <div className="auth-loading-orb" />
      </div>
    );
  }

  // Not authenticated — show login
  if (!isAuthenticated) {
    return <LoginPage />;
  }

  return (
    <div className="solomon-app" data-theme={theme}>

      {/* API / logic error toast */}
      {error && <ErrorToast key={error} message={error} onDismiss={() => dispatch({ type: ACTIONS.CLEAR_ERROR })} />}

      {/* Non-error notice (same toast, announced politely — never an alert) */}
      {notice && <ErrorToast key={notice} message={notice} variant="success" onDismiss={() => dispatch({ type: ACTIONS.CLEAR_NOTICE })} />}

      {/* Speech recognition error toast */}
      {sttError && <ErrorToast key={sttError} message={sttError} />}

      {/* Three-column app layout: ResearchPanel (persistent left) + ChatInterface (main).
          ConversationSidebar stays a position:fixed drawer that overlays above. */}
      <div className="app-layout">
      <ResearchPanel
        isOpen={researchPanelOpen}
        onToggle={toggleResearchPanel}
        onAutoOpen={handleAutoOpenResearchPanel}
      />

      <WholesaleWorkspace
        isOpen={wholesalePanelOpen}
        initialLeadId={pendingLeadId}
        onConsumeInitialLeadId={() => setPendingLeadId(null)}
        onClose={toggleWholesalePanel}
      />

      <AutomationsWorkspace
        isOpen={automationsPanelOpen}
        initialTab={automationsTab}
        initialAutomationId={pendingAutomationId}
        onConsumeInitialAutomationId={() => setPendingAutomationId(null)}
        onClose={toggleAutomationsPanel}
      />

      {/* Primary interface — Solomon IS the screen */}
      <ChatInterface
        messages={messages}
        isThinking={isThinking}
        thinkingModel={thinkingModel}
        isSpeaking={isSpeaking}
        isListening={sttIsListening}
        isError={orbError}
        isLoadingHistory={isLoadingHistory}
        interimText={interimText}
        theme={theme}
        userName={user?.name || 'there'}

        /* Voice panel props */
        voiceEnabled={voiceEnabled}
        selectedVoiceId={selectedVoiceId}
        fetchVoices={fetchVoices}
        onToggleVoice={handleToggleVoice}
        onSelectVoice={setSelectedVoiceId}
        onStopSpeaking={stopSpeaking}
        sttIsSupported={sttIsSupported}

        /* File upload */
        onFileUpload={handleFileUpload}
        uploadProgress={uploadProgress}
        onCancelUpload={cancelUpload}

        /* Conversation sidebar */
        sidebarOpen={sidebarOpen}
        onToggleSidebar={toggleSidebar}
        onSelectConversation={handleSelectConversation}
        activeConversationId={conversationId}

        /* Settings panel */
        settingsOpen={settingsOpen}
        settingsSection={settingsSection}
        onToggleSettings={toggleSettings}
        onOpenSettings={openSettings}

        /* Unified tool modal state (signals + memory + visual tools) */
        activeTool={activeTool}
        onSetActiveTool={setActiveTool}

        /* The three workspace panels are rendered above and opened from the
           Capabilities map — ChatInterface carries no toggle for them. */

        /* Capability surface */
        capabilityState={capabilityState}
        capabilitiesOpen={capabilitiesOpen}
        onOpenCapabilities={openCapabilities}
        onCloseCapabilities={closeCapabilities}
        onInvokeCapability={handleCapabilityInvoke}

        /* Integrations */
        gmailConnected={gmailConnected}
        onConnectGmail={connectGmail}
        onDisconnectGmail={disconnectGmail}

        /* Core handlers */
        onSend={sendMessage}
        onStop={stopGeneration}
        onVoiceToggle={toggleListening}
        onNewChat={newConversation}
        onThemeToggle={toggleTheme}
        onRegenerateWithMode={regenerateWithMode}
        onEditMessage={editMessage}
        pendingDraft={pendingDraft}
        onDraftConsumed={consumeDraft}
        onLogout={handleLogout}

        /* Ephemeral chat attachments (sticky-for-conversation) */
        onDocumentAttach={attachDocument}
        attachments={attachments}
        onClearAttachment={clearAttachment}
        onClearAllAttachments={clearAttachments}

        /* Pending images (ephemeral one-turn vision attachments) */
        onImageAttach={attachImage}
        pendingImages={pendingImages}
        onClearPendingImage={clearPendingImage}

        /* Phase 2A: action guardrails */
        pendingActions={visiblePendingActions(pendingActions, conversationId)}
        activeUndoBanner={activeUndoBanner}
        onApproveAction={approveAction}
        onCancelAction={cancelAction}
        onUndoAction={undoAction}
        onDismissUndoBanner={dismissUndoBanner}
      />
      </div>

      {/* Teach Solomon — mounted ONCE, outside the activeTool switch, and
          hidden rather than unmounted when closed so an in-flight ingest keeps
          running and stays visible when reopened. */}
      <LearnPanel isOpen={learnOpen} onClose={closeLearn} />
    </div>
  );
}

// ============================================================
// ERROR TOAST
// ============================================================

function ErrorToast({ message, onDismiss, timeout = 8000, variant = 'error' }) {
  const [visible, setVisible] = useState(true);
  const [fading, setFading]   = useState(false);

  useEffect(() => {
    // Critical errors (server/session) get 10s, standard errors 8s. A success
    // notice is never critical — its own keywords must not extend it.
    const isCritical = variant !== 'success' && /session|refresh|server|unavailable/i.test(message);
    const ms = isCritical ? 10000 : timeout;

    const fadeTimer = setTimeout(() => setFading(true), ms);
    const hideTimer = setTimeout(() => {
      setVisible(false);
      onDismiss?.();
    }, ms + 250); // 250ms for fade-out animation

    return () => { clearTimeout(fadeTimer); clearTimeout(hideTimer); };
  }, [message, timeout, onDismiss, variant]);

  if (!visible) return null;

  return (
    <div
      role={variant === 'success' ? 'status' : 'alert'}
      aria-live={variant === 'success' ? 'polite' : 'assertive'}
      className={`error-toast${variant === 'success' ? ' error-toast--success' : ''}${fading ? ' error-toast--fading' : ''}`}
    >
      <span>{variant === 'success' ? '✓' : '⚠'} {message}</span>
      <button
        onClick={() => { setVisible(false); onDismiss?.(); }}
        className="error-toast__dismiss"
        aria-label={variant === 'success' ? 'Dismiss notice' : 'Dismiss error'}
      >
        &#10005;
      </button>
    </div>
  );
}
